import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LayoutDashboard, Users } from "lucide-react";
import PrivateLayout from "@/components/layout/auth/PrivateLayout";
import { TabsHeader } from "@/components/ui/tabs-header";
import { Tabs, ProfileTabsContent } from "@/components/ui/tabs";
import { useSidebar } from "@/contexts/SidebarContext";
import { useI18n } from "@/hooks/useI18n";
import { useAuth } from "@/hooks/useAuth";
import { AgencyOverviewTab } from "@/lib/dashboard/AgencyOverviewTab";
import { AdvisorMembersTab } from "@/lib/dashboard/AdvisorMembersTab";
import { AgencyDashboardPeriod } from "@/integrations/supabase/rpc-types";
import type { TranslationKey } from "@/types/i18n.types";

const PERIOD_OPTIONS: { value: AgencyDashboardPeriod; labelKey: TranslationKey }[] = [
  { value: "7d", labelKey: "dashboard.overview.period7d" },
  { value: "30d", labelKey: "dashboard.overview.period30d" },
  { value: "90d", labelKey: "dashboard.overview.period90d" },
  { value: "ytd", labelKey: "dashboard.overview.periodYtd" },
];

const AgencyOverview = () => {
  const { t } = useI18n();
  const { isSidebarCollapsed } = useSidebar();
  const { userRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState<AgencyDashboardPeriod>("30d");

  const activeTab = searchParams.get("tab") || "overview";
  const handleTabChange = (value: string) => setSearchParams({ tab: value });

  const showAdvisorsTab = userRole !== "travel_advisor_member" && userRole !== "travel_designer";

  const tabs = [
    { value: "overview", label: t("dashboard.overview.tabs.overview"), icon: LayoutDashboard },
    ...(showAdvisorsTab ? [{ value: "advisors", label: t("dashboard.overview.tabs.advisors"), icon: Users }] : []),
  ];

  return (
    <PrivateLayout
      pageTitle={t("dashboard.overview.pageTitle")}
      pageDescription={t("dashboard.overview.pageDescription")}
      showBackButton={false}
      showCtaButton={false}
    >
      <TabsHeader
        activeTab={activeTab}
        onTabChange={handleTabChange}
        tabs={tabs}
        sidebarCollapsed={isSidebarCollapsed}
      />

      <div className="max-w-7xl mt-4 mx-auto space-y-4">
        {/* Period selector */}
        <div className="flex justify-end">
          <div className="inline-flex items-center bg-gray-100 rounded-2xl p-1 gap-0.5">
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setPeriod(opt.value)}
                className={
                  opt.value === period
                    ? "px-3 py-1.5 text-sm font-medium text-gray-900 bg-white rounded-xl shadow-sm transition-all"
                    : "px-3 py-1.5 text-sm text-gray-500 rounded-xl hover:text-gray-700 transition-all"
                }
              >
                {t(opt.labelKey)}
              </button>
            ))}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <ProfileTabsContent value="overview">
            <AgencyOverviewTab period={period} />
          </ProfileTabsContent>
          {showAdvisorsTab && (
            <ProfileTabsContent value="advisors">
              <AdvisorMembersTab period={period} />
            </ProfileTabsContent>
          )}
        </Tabs>
      </div>
    </PrivateLayout>
  );
};

export default AgencyOverview;
